import { useEffect, useState, useCallback } from "react";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDateTime } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import { listBlockedIps, blockIp, unblockIp } from "../../lib/adminApi";

function IpBlocksInner() {
  const { toast } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listBlockedIps();
      setItems(d?.items || []);
    } catch (e) {
      toast(e.message || "Failed to load blocklist", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!ip.trim()) return;
    setSaving(true);
    try {
      await blockIp(ip.trim(), reason.trim() || undefined);
      toast("IP blocked", "success");
      setIp(""); setReason("");
      load();
    } catch (err) {
      toast(err.message === "invalid_ip" ? "That doesn't look like a valid IP" : (err.message || "Failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    try {
      await unblockIp(row.id);
      toast(`Unblocked ${row.ip}`, "success");
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">IP Blocker</h1>
          <p className="page-sub">{items.length} blocked address{items.length === 1 ? "" : "es"}</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <form onSubmit={add} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="field" style={{ margin: 0, flex: "1 1 200px" }}>
            <label>IP address</label>
            <input className="input" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="203.0.113.42" />
          </div>
          <div className="field" style={{ margin: 0, flex: "2 1 240px" }}>
            <label>Reason (optional)</label>
            <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Abuse / spam" />
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            <Icon name="shield-off" size={14} /> {saving ? "Blocking…" : "Block IP"}
          </button>
        </form>
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 10, marginBottom: 0 }}>
          Blocked IPs are rejected before reaching the API (including sign-in). Changes apply within ~30 seconds.
        </p>
      </div>

      {loading ? (
        <TableSkeleton columns={4} rows={4} />
      ) : items.length === 0 ? (
        <EmptyState title="No blocked IPs" desc="Add an address above to block it." icon="shield" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>IP address</th>
                <th>Reason</th>
                <th>Blocked</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td data-label="IP" style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13 }}>{row.ip}</td>
                  <td data-label="Reason" style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{row.reason || "—"}</td>
                  <td data-label="Blocked" style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtDateTime(row.createdAt)}</td>
                  <td data-label="">
                    <button className="btn btn-ghost btn-sm" onClick={() => remove(row)}>
                      <Icon name="shield-check" size={13} /> Unblock
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminIpBlocks() {
  return (
    <AdminPageBoundary>
      <IpBlocksInner />
    </AdminPageBoundary>
  );
}
